import CheckIcon from '@mui/icons-material/Check';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import { EVENT_CSS_VARS } from '../styles/cssVariables';

/**
 * Main event wrapper - consumes CSS variables for variant styling
 * Merged StyledEventBox with first child Box to eliminate unnecessary wrapper
 *
 * Responsive sizing via CSS Container Queries (progressive enhancement):
 * - Only active in day/week views (month view excluded via .rbc-time-view selector)
 * - Default (fallback): 13px font, 6px/10px padding for browsers without container query support
 * - Small events (<27px): Compact sizing with reduced padding and font
 */
export const EventWrapper = styled(Box)({
  // Layout (merged from StyledEventBox + first child Box)
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'row', // Changed from 'column' (StyledEventBox) to 'row' (child Box)
  gap: '8px', // Merged from child Box (gap: 1 = 8px)
  overflow: 'hidden', // Merged from child Box
  borderRadius: '6px',
  padding: '6px 10px',
  boxSizing: 'border-box',

  // Positioning
  position: 'relative',
  zIndex: 'auto',

  // Transitions
  transition: 'all 0.2s ease',

  // Typography (static - default fallback for browsers without container queries)
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: 1.4,

  // Appearance (dynamic via CSS variables with fallbacks)
  backgroundColor: `var(${EVENT_CSS_VARS.color.bg}, #6C00CC)`,
  color: `var(${EVENT_CSS_VARS.color.text}, #ffffff)`,
  outline: `var(${EVENT_CSS_VARS.outline.width}, 0px) var(${EVENT_CSS_VARS.outline.style}, solid) var(${EVENT_CSS_VARS.color.border}, transparent)`,
  outlineOffset: `calc(-1 * var(${EVENT_CSS_VARS.outline.width}, 0px))`,
  borderLeftWidth: `var(${EVENT_CSS_VARS.borderLeft.width}, 0px)`,
  borderLeftStyle: 'solid', // Fixed style for left border
  borderLeftColor: `var(${EVENT_CSS_VARS.color.border}, transparent)`,
  boxShadow: `var(${EVENT_CSS_VARS.shadow}, none)`,

  // Cursor for interactivity
  cursor: 'pointer',

  // Hover state - increase z-index to show above other events
  '&:hover': {
    zIndex: 10,
  },

  // Container query: Small events (< 27px height) - only active in day/week views
  '@container (height < 27px)': {
    fontSize: 'clamp(12px, 2.8cqh, 13px)',
    padding: 'clamp(1px, 15cqh, 6px) 10px',
    lineHeight: 1.2,
  },
}) as typeof Box;

/**
 * Title box wrapper - contains title row and time label
 */
export const TitleWrapper = styled(Box)({
  flexGrow: 1,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}) as typeof Box;

/**
 * Title and confirmation icon row
 */
export const TitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '4px', // 0.5 * 8px
}) as typeof Box;

/**
 * Title text with ellipsis overflow
 */
export const TitleText = styled(Box)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}) as typeof Box;

/**
 * Time label shown below title (hidden in month view)
 * Scales responsively with container queries in day/week views
 */
export const TimeLabel = styled(Box)({
  fontSize: '11px',
  opacity: 0.9,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginTop: '2px',

  // Container query responsive sizing - only active in day/week views
  '@container (height < 27px)': {
    display: 'none', // Hide time label on very small events
  },
}) as typeof Box;

/**
 * Icon box - contains event icon
 * Scales responsively with container queries in day/week views
 *
 * requires component prop of SvgIcon or similar
 */
export const Icon = styled(Box)({
  fontSize: '18px',
  flexShrink: 0, // Prevent icon from shrinking

  // Container query responsive sizing - only active in day/week views
  '@container (height < 27px)': {
    fontSize: 'clamp(12px, 80cqh, 15px)',
  },
}) as typeof Box;

/**
 * confirmation checkmark
 * Scales responsively with container queries in day/week views
 */
export const ConfirmedIcon = styled(CheckIcon)({
  fontSize: '14px',
  flexShrink: 0, // Prevent icon from shrinking

  // Container query responsive sizing - only active in day/week views
  '@container (height < 27px)': {
    fontSize: 'clamp(10px, 70cqh, 13px)',
  },
}) as unknown as typeof CheckIcon;
